// src/pages/ProductionToolkit/services/productionService.js

import { db } from "../../../services/firebase";
import {
    collection,
    getDocs,
    getDoc,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    query,
    where,
} from "firebase/firestore";
import {
    getNextInternalBatchNo,
    isBatchNoLike,
    parseBatchNo,
    deriveSkuRoot,
} from "../utils/batchNumberUtils";
import unifiedStockService, { SYNC_TARGET } from "../../../services/unifiedStockService.js";
import { MOVEMENT_TYPE, SOURCE_SYSTEM } from "../../../services/stockMovementService.js";
import unifiedProductService from "../../../services/unifiedProductService.js";

/** ──────────────────────────────────────────────────────────────────────────────
 *  TYPE TAXONOMY (single source of truth for the app)
 *  Keys: dry, wet, casings, packaging, meat, fish
 *  - BATCH_ELIGIBLE_TYPES: dry, wet, meat, fish
 *  - Includes robust normalization from legacy values ("ingredient", "additional", etc.)
 *  - Includes heuristics for "casings/clips/nets"
 *  - Default UOMs applied on create/update when missing
 *  ───────────────────────────────────────────────────────────────────────────── */
export const TYPE_TAXONOMY = {
    dry: { key: "dry", label: "Dry Ingredients", defaultUom: "kg" },
    wet: { key: "wet", label: "Wet Ingredients", defaultUom: "kg" },
    casings: { key: "casings", label: "Casings/Clips/Nets", defaultUom: "unit" },
    packaging: { key: "packaging", label: "Packaging", defaultUom: "unit" },
    meat: { key: "meat", label: "Meat", defaultUom: "kg" },
    fish: { key: "fish", label: "Fish", defaultUom: "kg" },
};
export const TYPE_KEYS = Object.keys(TYPE_TAXONOMY);
export const TYPE_LABEL_FROM_KEY = Object.fromEntries(
    TYPE_KEYS.map(k => [k, TYPE_TAXONOMY[k].label])
);
export const TYPE_DEFAULT_UOM = Object.fromEntries(
    TYPE_KEYS.map(k => [k, TYPE_TAXONOMY[k].defaultUom])
);
export const BATCH_ELIGIBLE_TYPES = ["dry", "wet", "meat", "fish"];

/** Normalize any incoming type to our 6 keys; returns a valid key. */
export function normalizeTypeKey(input) {
    const raw = String(input || "").trim().toLowerCase();
    if (TYPE_KEYS.includes(raw)) return raw;

    // Common legacy values
    if (raw === "ingredient") return "dry";               // legacy bucket
    if (raw === "additional" || raw === "component") return "packaging";
    if (raw === "intermediate") return "dry";             // best-effort
    if (raw === "final" || raw === "product") return "packaging"; // finals aren't deliveries; safe bucket
    if (raw === "meat") return "meat";
    if (raw === "fish") return "fish";

    // String heuristics
    if (/casing|clip|net/i.test(raw)) return "casings";
    if (/pack|box|tray|film|pouch|label/i.test(raw)) return "packaging";
    if (/wet|brine|water|oil|vinegar|glucose|molasses|syrup/i.test(raw)) return "wet";

    // Unknown → default to "dry" so we never crash
    return "dry";
}

/** Small helpers */
function userLabel(user) {
    return user?.displayName || user?.email || user?.uid || "system";
}
function toTimestampMaybe(dateLike) {
    if (!dateLike) return null;
    const d = new Date(dateLike);
    return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}
function tsToMillis(x) {
    try {
        if (!x) return 0;
        if (typeof x?.toMillis === "function") return x.toMillis();
        if (typeof x?.toDate === "function") return x.toDate().getTime();
        if (x?.seconds != null) return x.seconds * 1000;
        const n = new Date(x).getTime();
        return Number.isFinite(n) ? n : 0;
    } catch {
        return 0;
    }
}
function normalizeSkuRootField(obj) {
    const v =
        obj?.skuRoot ||
        obj?.productCode ||
        obj?.sku ||
        obj?.code ||
        obj?.finalSku ||
        "";
    return deriveSkuRoot(v || "GEN");
}
function clamp(n, lo, hi) {
    const x = Number(n || 0);
    if (!Number.isFinite(x)) return lo;
    return Math.max(lo, Math.min(hi, x));
}
function pad4(x) {
    const s = String(x ?? "").replace(/\D+/g, "");
    if (!s) return "";
    return s.padStart(4, "0").slice(-4);
}

// HARDCODED_GTIN_SKU map removed - now using unifiedProductService.resolveSkuFromGtin()
// All GTIN mappings are stored in Firestore 'StandardItems' collection

/** ------------------------------ Stage + Recipe helpers ------------------------------ */
const KIND_TO_STAGE = {
    cure: "cured",
    smoke: "smoked",
    slice: "finished",
    pack: "finished",
    chill: "cured",
    dry: "cured",
};
function stagesFromRecipe(recipe) {
    if (!recipe?.actions?.length) return ["wip", "cured", "smoked", "finished"];
    const out = ["wip"];
    for (const a of recipe.actions) {
        const s = KIND_TO_STAGE[String(a?.kind || "").toLowerCase()];
        if (s && !out.includes(s)) out.push(s);
    }
    if (!out.includes("finished")) out.push("finished");
    return out;
}
function minHoursForStage(recipe, stage) {
    if (!recipe?.actions?.length) return 0;
    const want = String(stage || "").toLowerCase();
    let maxH = 0;
    for (const a of recipe.actions) {
        const mapped = KIND_TO_STAGE[String(a?.kind || "").toLowerCase()];
        if (mapped === want && a?.params?.hours) {
            maxH = Math.max(maxH, Number(a.params.hours) || 0);
        }
    }
    return maxH;
}

/** Try to find a recipe for a batch (by recipeId, or skuRoot, or name fallback). */
async function resolveRecipeForBatchDoc(batch) {
    try {
        if (!batch) return null;
        // 1) direct
        if (batch.recipeId) {
            const r = await getDoc(doc(db, "recipes", batch.recipeId));
            if (r.exists()) return { id: r.id, ...r.data() };
        }
        // 2) all + match root
        const recs = await listRecipes();
        const rootFromBatch =
            (batch.skuRoot ||
                (typeof batch.productCode === "string" &&
                    unifiedProductService.getSKURoot(batch.productCode)) ||
                "") + "";
        if (rootFromBatch) {
            const hit = recs.find(
                (r) =>
                    String(r.skuRoot || r.root || "")
                        .toUpperCase()
                        .trim() === String(rootFromBatch).toUpperCase().trim()
            );
            if (hit) return hit;
        }
        // 3) fuzzy by product name
        const needle = String(batch.productName || batch.productCode || "").toLowerCase();
        if (needle) {
            const byName = recs.find((r) => String(r.name || "").toLowerCase().includes(needle));
            if (byName) return byName;
        }
        return null;
    } catch {
        return null;
    }
}
async function resolveRecipeForBatchData(batchData) {
    try {
        if (batchData?.recipeId) {
            const r = await getDoc(doc(db, "recipes", batchData.recipeId));
            if (r.exists()) return { id: r.id, ...r.data() };
        }
        const recs = await listRecipes();
        const root = normalizeSkuRootField(batchData);
        const hit =
            recs.find(
                (r) =>
                    String(r.skuRoot || r.root || "")
                        .toUpperCase()
                        .trim() === String(root).toUpperCase().trim()
            ) || null;
        return hit;
    } catch {
        return null;
    }
}

/** ------------------------------ Ingredient Batches ------------------------------ */
const ingredientBatchCol = collection(db, "IngredientBatches");

export async function listIngredientBatches() {
    const snap = await getDocs(ingredientBatchCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listIngredientBatchesByCode(codeLike) {
    const code = String(codeLike || "").toUpperCase();
    const all = await listIngredientBatches();
    return all.filter(
        (b) => String(b.ingredientCode || "").toUpperCase() === code
    );
}

export async function listOpenIngredientBatchesByCode(codeLike) {
    const list = await listIngredientBatchesByCode(codeLike);
    return list
        .filter((b) => Number(b.remainingKg || 0) > 0)
        .sort(
            (a, b) => (a.receivedDate?.seconds || 0) - (b.receivedDate?.seconds || 0)
        );
}

export async function createIngredientBatch(batchData, user) {
    const now = Timestamp.now();
    const typeNorm = normalizeTypeKey(batchData.type || batchData.itemType || batchData.kind || batchData.category);
    const payload = {
        ...batchData,
        internalBatchNo: batchData.internalBatchNo || batchData.batchNo || null,
        ingredientCode:
            batchData.ingredientCode || batchData.code || batchData.sku || "",
        quantityKg: Number(batchData.quantityKg ?? 0),
        remainingKg: Number(batchData.remainingKg ?? batchData.quantityKg ?? 0),
        reservedKg: Number(batchData.reservedKg ?? 0),
        receivedDate: toTimestampMaybe(batchData.receivedDate) ?? Timestamp.now(),
        expiryDate: toTimestampMaybe(batchData.expiryDate),
        createdBy: userLabel(user),
        createdAt: now,
        type: typeNorm,
        uom: batchData.uom || TYPE_DEFAULT_UOM[typeNorm] || "kg",
    };
    const ref = await addDoc(ingredientBatchCol, payload);
    return ref.id;
}
export async function updateIngredientBatch(id, batchData, user) {
    const ref = doc(db, "IngredientBatches", id);

    const base = {};
    Object.entries(batchData || {}).forEach(([k, v]) => {
        if (v !== undefined) base[k] = v;
    });

    if (base.quantityKg != null) base.quantityKg = Number(base.quantityKg);
    if (base.remainingKg != null) base.remainingKg = Number(base.remainingKg);
    if (base.reservedKg != null) base.reservedKg = Number(base.reservedKg);

    if ("receivedDate" in base) base.receivedDate = toTimestampMaybe(base.receivedDate);
    if ("expiryDate" in base) base.expiryDate = toTimestampMaybe(base.expiryDate);
    if (base.type != null) base.type = normalizeTypeKey(base.type);
    if (base.uom == null && base.type) base.uom = TYPE_DEFAULT_UOM[normalizeTypeKey(base.type)] || "kg";

    base.updatedBy = userLabel(user);
    base.updatedAt = Timestamp.now();

    await updateDoc(ref, base);
}

export async function deleteIngredientBatch(id) {
    await deleteDoc(doc(db, "IngredientBatches", id));
}
export async function updateIngredientBatchSchedule(
    id,
    { receivedDate, expectedEndDate }
) {
    const ref = doc(db, "IngredientBatches", id);
    const payload = {
        receivedDate: toTimestampMaybe(receivedDate),
        expectedEndDate: expectedEndDate ? toTimestampMaybe(expectedEndDate) : null,
        updatedAt: Timestamp.now(),
    };
    await updateDoc(ref, payload);
}

/** reserve qty for processing (remainingKg → reservedKg) */
export async function reserveIngredientForProcessing(
    ingredientBatchId,
    qtyKg,
    user
) {
    const ref = doc(db, "IngredientBatches", ingredientBatchId);
    const s = await getDoc(ref);
    if (!s.exists()) throw new Error("Ingredient batch not found");
    const d = s.data();
    const remain = Number(d.remainingKg ?? 0);
    const reserve = Number(d.reservedKg ?? 0);
    const q = Number(qtyKg || 0);
    if (q <= 0) return { remainingKg: remain, reservedKg: reserve };
    if (remain < q) throw new Error("Not enough remaining to reserve");
    await updateDoc(ref, {
        remainingKg: remain - q,
        reservedKg: reserve + q,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
    return { remainingKg: remain - q, reservedKg: reserve + q };
}

/** release reservation (reservedKg → remainingKg) */
export async function releaseIngredientReservation(
    ingredientBatchId,
    qtyKg,
    user
) {
    const ref = doc(db, "IngredientBatches", ingredientBatchId);
    const s = await getDoc(ref);
    if (!s.exists()) throw new Error("Ingredient batch not found");
    const d = s.data();
    const remain = Number(d.remainingKg ?? 0);
    const reserve = Number(d.reservedKg ?? 0);
    const q = Math.max(0, Number(qtyKg || 0));
    const release = Math.min(q, reserve);
    await updateDoc(ref, {
        remainingKg: remain + release,
        reservedKg: reserve - release,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
    return { remainingKg: remain + release, reservedKg: reserve - release };
}

/** ------------------------------ Packaging Stock (finals) ------------------------------ */
/** We maintain:
 *  A) Quick totals:   stockLevels / "stock levels"  (doc)  -> field "stock" : { [finalSku]: units }
 *  B) Per-lot counts: stockLevels / "stock levels" / lots (subcol) -> docId "<sku>__<code>"
 */
const STOCKLEVELS_COL = "stockLevels";
const STOCKLEVELS_DOC_ID = "stock levels";
const stockLevelsRef = doc(db, STOCKLEVELS_COL, STOCKLEVELS_DOC_ID);

async function ensureStockLevelsDoc() {
    const snap = await getDoc(stockLevelsRef);
    if (!snap.exists()) {
        await setDoc(stockLevelsRef, { stock: {} }, { merge: true });
    }
}

/** 
 * Adjust total units for a final SKU (can be negative)
 * ENHANCED WITH TWO-PHASE COMMIT: Now syncs warehouse AND e-commerce stock
 */
export async function adjustFinalSkuUnits(finalSku, deltaUnits, user) {
    const sku = String(finalSku || "").toUpperCase();
    if (!sku || !Number.isFinite(Number(deltaUnits))) {
        console.warn(`[Stock] Invalid adjustFinalSkuUnits call: sku=${finalSku}, delta=${deltaUnits}`);
        return;
    }
    
    const delta = Number(deltaUnits);
    const isProduction = delta > 0;
    
    console.log(`[Stock] adjustFinalSkuUnits - ${isProduction ? 'Production' : 'Packing'} operation:`, {
        sku,
        deltaUnits: delta,
        changeType: isProduction ? 'INCREASE (Production)' : 'DECREASE (Packing/Shipping)',
        user: user?.email || user?.displayName || 'system',
        timestamp: new Date().toISOString()
    });
    
    // ===== PHASE 5 MIGRATION: Two-phase commit via unifiedStockService =====
    // This syncs BOTH warehouse (stockLevels) AND e-commerce (products_default)
    // No more stock divergence between systems!
    try {
        const resolvedSourceSystem =
            user?.sourceSystem || (delta > 0 ? SOURCE_SYSTEM.PRODUCTION : SOURCE_SYSTEM.PACKERS);
        const resolvedSourceId = user?.sourceId || user?.source || null;

        const result = await unifiedStockService.updateStock(sku, delta, {
            type: isProduction ? MOVEMENT_TYPE.PRODUCTION : MOVEMENT_TYPE.PACKING,
            sourceSystem: resolvedSourceSystem,
            sourceId: resolvedSourceId,  // batchId/orderId if available
            userId: user?.email || user?.displayName || 'system',
            reason: isProduction ? 'Production packaging' : 'Packing operation',
            tier: 'default',  // TODO: Support multi-tier in future
            syncTo: SYNC_TARGET.WAREHOUSE_ONLY,  // ← Warehouse stock only - e-commerce stock is managed separately for customer display
            allowNegative: false,
            additionalData: {
                originalCaller: 'adjustFinalSkuUnits',
                userSource: resolvedSourceId || 'unknown'
            }
        });

        console.log(`[Stock] ✅ Two-phase commit completed for ${sku}:`, {
            warehouse: result.warehouse,
            ecommerce: result.ecommerce,
            syncSuccess: result.success,
            rollback: result.rollback
        });

        return result;

    } catch (error) {
        console.error(`[Stock] ❌ Two-phase commit failed for ${sku}:`, error);
        throw new Error(`Failed to update stock: ${error.message}`);
    }
}

/** 
 * Read all quick totals (SKU -> units)
 * PHASE 5 MIGRATION: Now uses unifiedStockService
 */
export async function getAllFinalSkuUnits() {
    console.log(`[Stock] Reading all final SKU units via unifiedStockService`);
    
    try {
        // Use unifiedStockService to get main stock (warehouse)
        const stock = await unifiedStockService.getMainStock({ useCache: false });
        
        console.log(`[Stock] Retrieved stock levels via unifiedStockService:`, {
        totalSkus: Object.keys(stock).length,
            skus: Object.keys(stock).slice(0, 10),
        sampleValues: Object.fromEntries(
            Object.entries(stock).slice(0, 5).map(([sku, units]) => [sku, units])
            )
    });
    
    return stock;
    } catch (error) {
        console.error(`[Stock] Failed to read stock levels:`, error);
        return {};
    }
}

/** Adjust per-lot units (sku + 4-digit code). */
export async function adjustFinalSkuLotUnits(
    finalSku,
    consumerCode,
    deltaUnits,
    meta = {},
    user
) {
    const sku = String(finalSku || "").toUpperCase();
    const code = String(consumerCode || "GEN").trim();
    if (!sku || !Number.isFinite(Number(deltaUnits))) {
        console.warn(`[Stock] Invalid adjustFinalSkuLotUnits call: sku=${finalSku}, code=${consumerCode}, delta=${deltaUnits}`);
        return;
    }

    const normalizedSku = sku;
    const lotCode = code;

    const lots = await unifiedStockService.getLotStock(normalizedSku, { includeFrozen: true, useCache: false });
    const match = Array.isArray(lots)
        ? lots.find(l => (l.lotCode || l.consumerCode || '').toUpperCase() === lotCode.toUpperCase())
        : null;
    const currentUnits = Number(match?.units || 0);
    const desiredUnits = currentUnits + Number(deltaUnits);
    const newUnits = Math.max(0, desiredUnits);

    if (desiredUnits < 0) {
        console.warn(`[Stock] Lot units would go negative; clamping to 0`, {
            sku: normalizedSku,
            lotCode,
        currentUnits,
        deltaUnits,
            desiredUnits
        });
    }

    await unifiedStockService.upsertLotStockEntry(normalizedSku, lotCode, newUnits, {
            productBatchId: meta.productBatchId || null,
        packSize: meta.packSize || null,
        source: meta.source || (Number(deltaUnits) > 0 ? 'production_packaging' : 'packing_consumption'),
        userId: user?.email || user?.displayName || 'system',
        createdAt: match?.createdAt || Timestamp.now(),
        isFrozen: match?.isFrozen === true
    });
}

/** List lots for a SKU (each lot is SKU + 4-digit code). */
export async function listFinalSkuLots(finalSku) {
    const sku = String(finalSku || "").toUpperCase();
    
    console.log(`[Stock] Listing lots for SKU via unifiedStockService: ${sku}`);
    const lots = await unifiedStockService.getLotStock(sku, { includeFrozen: true, useCache: false });
    return Array.isArray(lots) ? lots : [];
}

/** Consume packaged stock (helper for shipping/adjustments). */
export async function consumeFinalSkuUnits(finalSku, units, consumerCode = null, user) {
    console.log(`[Stock] Consuming final SKU units:`, {
        finalSku,
        units,
        consumerCode,
        user: user?.email || user?.displayName || 'system',
        timestamp: new Date().toISOString()
    });

    const delta = -Math.abs(Number(units || 0));
    await adjustFinalSkuUnits(finalSku, delta, user);
    if (consumerCode) {
        await adjustFinalSkuLotUnits(finalSku, consumerCode, delta, {}, user);
    }

    console.log(`[Stock] Successfully consumed ${units} units of ${finalSku}${consumerCode ? ` (lot: ${consumerCode})` : ''}`);
}

/** Consume from multiple lots (for hybrid traceability) */
export async function consumeFromLotAllocations(allocations = [], user) {
    // allocations: [{ finalSku, lotCode, unitsToConsume, productBatchId }]
    console.log(`[Stock] Consuming from lot allocations:`, {
        totalAllocations: allocations.length,
        allocations: allocations.map(alloc => ({
            sku: alloc.finalSku,
            lotCode: alloc.lotCode,
            unitsToConsume: alloc.unitsToConsume,
            productBatchId: alloc.productBatchId
        })),
        user: user?.email || user?.displayName || 'system',
        timestamp: new Date().toISOString()
    });

    try {
        for (const alloc of allocations) {
            const delta = -Math.abs(Number(alloc.unitsToConsume || 0));
            
            console.log(`[Stock] Processing allocation:`, {
                sku: alloc.finalSku,
                lotCode: alloc.lotCode,
                unitsToConsume: alloc.unitsToConsume,
                delta,
                productBatchId: alloc.productBatchId
            });
            
            // Decrement lot-specific stock
            await adjustFinalSkuLotUnits(
                alloc.finalSku, 
                alloc.lotCode, 
                delta, 
                { productBatchId: alloc.productBatchId },
                user
            );
            
            // Also decrement total
            await adjustFinalSkuUnits(alloc.finalSku, delta, user);
        }
        
        console.log(`[Stock] Successfully consumed from ${allocations.length} lot allocations`);
    } catch (error) {
        console.error("[Stock] Failed to consume from lot allocations:", error);
        throw error;
    }
}

/** ------------------------------ Product Batches ------------------------------ */
const productBatchCol = collection(db, "ProductBatches");

export async function listProductBatches() {
    const snap = await getDocs(productBatchCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function getProductBatchById(id) {
    const s = await getDoc(doc(db, "ProductBatches", id));
    return s.exists() ? { id: s.id, ...s.data() } : null;
}

export async function createProductBatch(batchData, user) {
    const now = Timestamp.now();
    const inferredRoot = normalizeSkuRootField(batchData);

    console.log(`[Production] Creating product batch:`, {
        batchData: {
            skuRoot: inferredRoot,
            qty: batchData.qty,
            stage: batchData.stage,
            produced: batchData.produced,
            expiry: batchData.expiry
        },
        user: user?.email || user?.displayName || 'system',
        timestamp: new Date().toISOString()
    });

    // Find matching recipe (if any) to seed min hours for wip
    const recipe = await resolveRecipeForBatchData(batchData);
    const wipMin = clamp(minHoursForStage(recipe, "wip"), 0, 1e6);
    const readyAt = wipMin > 0
        ? Timestamp.fromMillis(Date.now() + wipMin * 3600 * 1000)
        : now;

    const payload = {
        ...batchData,
        skuRoot: inferredRoot,
        qty: Number(batchData.qty ?? 0),
        remainingKg: Number(batchData.remainingKg ?? batchData.qty ?? 0),
        stage: batchData.stage || "wip", // planned|wip|cured|smoked|finished|packaged
        stageMeta: {
            wip: {
                startedAt: now,
                minHours: wipMin,
                readyAt,
            },
        },
        produced: toTimestampMaybe(batchData.produced) ?? Timestamp.now(),
        expiry: toTimestampMaybe(batchData.expiry),
        createdBy: userLabel(user),
        createdAt: now,
    };

    console.log(`[Production] Product batch payload:`, {
        skuRoot: payload.skuRoot,
        qty: payload.qty,
        remainingKg: payload.remainingKg,
        stage: payload.stage,
        wipMinHours: wipMin,
        readyAt: readyAt.toDate().toISOString(),
        produced: payload.produced?.toDate().toISOString(),
        expiry: payload.expiry?.toDate().toISOString()
    });
    const ref = await addDoc(productBatchCol, payload);

    // Log entering first stage (optional but useful for timelines)
    try {
        await logAction(
            "ProductBatches",
            ref.id,
            {
                actionId: "enter_stage",
                actionName: "Entered stage",
                meta: { stage: "wip", minHours: wipMin, readyAt },
            },
            user
        );
    } catch {
        /* ignore log failures */
    }

    return ref.id;
}
export async function updateProductBatch(id, batchData, user) {
    const ref = doc(db, "ProductBatches", id);

    const base = {};
    Object.entries(batchData || {}).forEach(([k, v]) => {
        if (v !== undefined) base[k] = v;
    });

    const hasRootish =
        base.skuRoot != null ||
        base.productCode != null ||
        base.sku != null ||
        base.code != null ||
        base.finalSku != null;

    if (hasRootish) {
        base.skuRoot = normalizeSkuRootField(base);
    }

    if (base.qty != null) base.qty = Number(base.qty);
    if (base.remainingKg != null) base.remainingKg = Number(base.remainingKg);

    if ("produced" in base) base.produced = toTimestampMaybe(base.produced);
    if ("expiry" in base) base.expiry = toTimestampMaybe(base.expiry);

    base.updatedBy = userLabel(user);
    base.updatedAt = Timestamp.now();

    await updateDoc(ref, base);
}

export async function deleteProductBatch(id) {
    await deleteDoc(doc(db, "ProductBatches", id));
}
export async function updateProductBatchSchedule(
    id,
    { produced, expectedEndDate }
) {
    const ref = doc(db, "ProductBatches", id);
    const payload = {
        produced: toTimestampMaybe(produced),
        expectedEndDate: expectedEndDate ? toTimestampMaybe(expectedEndDate) : null,
        updatedAt: Timestamp.now(),
    };
    await updateDoc(ref, payload);
}

/**
 * Set/advance stage with time gates + reason capture.
 * Backward compatible:
 *   setProductBatchStage(id, stage, user)
 *   setProductBatchStage(id, stage, { force, reason, actor }, user)
 */
export async function setProductBatchStage(id, nextStageArg, maybeOpts, maybeUser) {
    const nextStage = String(nextStageArg || "").toLowerCase() || "wip";

    // Back-compat arg resolution
    let opts = {};
    let user = null;
    if (maybeOpts && (Object.prototype.hasOwnProperty.call(maybeOpts, "force") ||
        Object.prototype.hasOwnProperty.call(maybeOpts, "reason") ||
        Object.prototype.hasOwnProperty.call(maybeOpts, "actor"))) {
        opts = maybeOpts || {};
        user = maybeUser || opts.actor || null;
    } else {
        user = maybeOpts || null;
        opts = {};
    }

    const ref = doc(db, "ProductBatches", id);
    const s = await getDoc(ref);
    if (!s.exists()) throw new Error("Product batch not found");
    const d = s.data();

    const current = String(d.stage || "wip").toLowerCase();

    // Resolve recipe + time gate
    const recipe = await resolveRecipeForBatchDoc({ id, ...d });
    const minHours = clamp(minHoursForStage(recipe, current), 0, 1e6);

    // Determine stage start
    const startedTs =
        d?.stageMeta?.[current]?.startedAt ||
        d?.produced ||
        d?.createdAt ||
        Timestamp.now();

    const now = Timestamp.now();
    const startedMs = tsToMillis(startedTs);
    const nowMs = tsToMillis(now);
    const elapsedHours = (nowMs - startedMs) / 3600000;

    const early = minHours > 0 && elapsedHours < minHours;
    const deficitHours = early ? Math.max(0, minHours - elapsedHours) : 0;

    if (early && !opts.force) {
        const err = new Error(
            `Stage "${current}" requires ${minHours}h. Only ${elapsedHours.toFixed(2)}h elapsed.`
        );
        err.code = "EARLY_STAGE_ADVANCE";
        err.minHours = minHours;
        err.elapsedHours = elapsedHours;
        err.deficitHours = deficitHours;
        throw err;
    }
    if (early && opts.force && !opts.reason) {
        const err = new Error("Reason required to override time requirement.");
        err.code = "REASON_REQUIRED";
        throw err;
    }

    // Log completion of current stage
    try {
        await logAction(
            "ProductBatches",
            id,
            {
                actionId: "complete_stage",
                actionName: `Completed stage: ${current}`,
                meta: {
                    stage: current,
                    minHours,
                    elapsedHours: Number(elapsedHours.toFixed(3)),
                    early,
                    reason: early ? String(opts.reason || "").trim() : null,
                },
            },
            user
        );
    } catch {
        /* ignore log failures */
    }

    // Update stageMeta (complete current, enter next)
    const nextStageKey = nextStage;
    const nextMin = clamp(minHoursForStage(recipe, nextStageKey), 0, 1e6);
    const readyAt = nextMin > 0
        ? Timestamp.fromMillis(nowMs + nextMin * 3600 * 1000)
        : now;

    const stageMeta = { ...(d.stageMeta || {}) };

    stageMeta[current] = {
        ...(stageMeta[current] || {}),
        startedAt: stageMeta[current]?.startedAt || startedTs,
        completedAt: now,
        minHours,
        durationMs: Math.max(0, nowMs - startedMs),
        earlyOverride: early
            ? {
                reason: String(opts.reason || "").trim(),
                by: userLabel(user),
                deficitHours: Number(deficitHours.toFixed(3)),
                at: now,
            }
            : stageMeta[current]?.earlyOverride || null,
    };

    stageMeta[nextStageKey] = {
        ...(stageMeta[nextStageKey] || {}),
        startedAt: now,
        minHours: nextMin,
        readyAt,
    };

    await updateDoc(ref, {
        stage: nextStageKey,
        stageMeta,
        updatedAt: Timestamp.now(),
        updatedBy: userLabel(user),
    });

    // Log entering next stage (optional)
    try {
        await logAction(
            "ProductBatches",
            id,
            {
                actionId: "enter_stage",
                actionName: "Entered stage",
                meta: { stage: nextStageKey, minHours: nextMin, readyAt },
            },
            user
        );
    } catch {
        /* ignore log failures */
    }

    return { stage: nextStageKey, stageMeta };
}

/** ------------------------------ Recipes ------------------------------ */
const recipesCol = collection(db, "recipes");

export async function listRecipes() {
    const snap = await getDocs(recipesCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function createRecipe(recipeData, user) {
    const ref = await addDoc(recipesCol, {
        ...recipeData,
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateRecipe(id, recipeData, user) {
    await updateDoc(doc(db, "recipes", id), {
        ...recipeData,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteRecipe(id) {
    await deleteDoc(doc(db, "recipes", id));
}

/** suggest recipes that use a specific code (ingredient/meat) */
export async function listRecipesThatUse(codeLike) {
    const code = String(codeLike || "").toUpperCase();
    const recs = await listRecipes();
    return recs.filter(
        (r) =>
            Array.isArray(r.items) &&
            r.items.some(
                (it) => String(it.code || it.name || "").toUpperCase() === code
            )
    );
}

/** ------------------------------ Deliveries ------------------------------ */
const deliveriesCol = collection(db, "Deliveries");

/** Status from remaining qty: received | partial | allocated */
export function deliveryStatusFromRemaining(totalQty, remainingQty) {
    const total = Number(totalQty) || 0;
    const rem = Number(remainingQty) || 0;
    if (rem <= 0) return "allocated";
    if (total > 0 && rem < total) return "partial";
    return "received";
}

/** Parse delivery expiry (Timestamp, seconds, or YYYY-MM-DD string) to YMD. */
function deliveryExpiryToYmd(expiryDate) {
    if (!expiryDate) return "";
    try {
        if (typeof expiryDate?.toDate === "function") {
            const ex = expiryDate.toDate();
            if (!isNaN(ex.getTime())) return ex.toISOString().slice(0, 10);
        }
        if (expiryDate?.seconds != null) {
            const ex = new Date(expiryDate.seconds * 1000);
            if (!isNaN(ex.getTime())) return ex.toISOString().slice(0, 10);
        }
        if (typeof expiryDate === "string") {
            const s = expiryDate.trim();
            if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
            const ex = new Date(s);
            if (!isNaN(ex.getTime())) return ex.toISOString().slice(0, 10);
        }
    } catch {
        /* ignore */
    }
    return "";
}

export async function listDeliveries() {
    const snap = await getDocs(deliveriesCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function createDelivery(deliveryData, user) {
    const now = Timestamp.now();
    const normalizedType = normalizeTypeKey(
        deliveryData.type || deliveryData.itemType || deliveryData.category
    );
    const totalQty = Number(deliveryData.totalQty ?? 0);
    const remainingQty = Number(deliveryData.remainingQty ?? deliveryData.totalQty ?? 0);
    const expiryTs =
        toTimestampMaybe(deliveryData.expiryDate) ||
        (deliveryData.expiryDate?.seconds != null ? deliveryData.expiryDate : null);

    const payload = {
        ...deliveryData,
        type: normalizedType,
        uom: deliveryData.uom || TYPE_DEFAULT_UOM[normalizedType] || "kg",
        totalQty,
        remainingQty,
        deliveryDate: toTimestampMaybe(deliveryData.deliveryDate) ?? Timestamp.now(),
        deliveryTime: deliveryData.deliveryTime || null,
        expiryDate: expiryTs,
        createdBy: userLabel(user),
        createdAt: now,
        status:
            deliveryData.status ||
            deliveryStatusFromRemaining(totalQty, remainingQty),
    };
    const ref = await addDoc(deliveriesCol, payload);
    return ref.id;
}

/**
 * Door receive + optional auto lot (IngredientBatches).
 * Does NOT touch warehouse stock, stockLevels, or stock movements.
 * All six types (dry/wet/meat/fish/casings/packaging) can create lots.
 */
export async function createDeliveryAndAllocateLot(deliveryData, user) {
    const deliveryId = await createDelivery(deliveryData, user);
    const qty = Number(deliveryData.totalQty ?? 0);
    const batchId = await allocateFromDelivery(deliveryId, qty, user);
    return { deliveryId, batchId };
}

export async function updateDelivery(id, data, user) {
    const ref = doc(db, "Deliveries", id);
    const normalizedType =
        data.type != null ? normalizeTypeKey(data.type) : undefined;
    const merged = { ...data };
    if (normalizedType) {
        merged.type = normalizedType;
        if (!merged.uom) merged.uom = TYPE_DEFAULT_UOM[normalizedType] || "kg";
    }
    if ("expiryDate" in merged) {
        merged.expiryDate = toTimestampMaybe(merged.expiryDate);
    }
    const payload = {
        ...merged,
        ...(merged.totalQty != null && {
            totalQty: Number(merged.totalQty),
            remainingQty: Number(merged.remainingQty ?? merged.totalQty),
        }),
        ...(merged.deliveryDate && { deliveryDate: toTimestampMaybe(merged.deliveryDate) }),
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    };
    if (payload.totalQty != null && payload.remainingQty != null && !payload.status) {
        payload.status = deliveryStatusFromRemaining(payload.totalQty, payload.remainingQty);
    }
    await updateDoc(ref, payload);
}
export async function deleteDelivery(id) {
    await deleteDoc(doc(db, "Deliveries", id));
}

/** allocate: create new ingredient batch from delivery (all type keys; no warehouse stock writes) */
export async function allocateFromDelivery(deliveryId, qtyToAllocate, user) {
    const deliveryRef = doc(db, "Deliveries", deliveryId);
    const snap = await getDoc(deliveryRef);
    if (!snap.exists()) throw new Error("Delivery not found");
    const data = snap.data();

    const remaining = Number(data.remainingQty ?? data.totalQty ?? 0);
    const total = Number(data.totalQty ?? remaining);
    const qty = Number(qtyToAllocate);
    if (remaining < qty) throw new Error("Not enough quantity on delivery");

    let dDate = data.deliveryDate?.toDate?.() || new Date(data.deliveryDate);
    if (!(dDate instanceof Date) || isNaN(dDate.getTime())) dDate = new Date();
    const dateStr = dDate.toISOString().slice(0, 10);

    const expiryStr = deliveryExpiryToYmd(data.expiryDate);
    const nextRemaining = remaining - qty;

    await updateDoc(deliveryRef, {
        remainingQty: nextRemaining,
        status: deliveryStatusFromRemaining(total, nextRemaining),
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });

    const batchNo = await getNextInternalBatchNo(
        data.itemName || data.productName || data.itemCode || "ING",
        data.itemSku || data.productName || data.itemCode || "ING",
        dateStr
    );

    const typeNorm = normalizeTypeKey(data.type || data.itemType || null);

    const batchData = {
        ingredientCode: data.itemName || data.productName || data.itemCode || "",
        internalBatchNo: batchNo,
        receivedDate: dateStr,
        expiryDate: expiryStr || null,
        quantityKg: qty,
        remainingKg: qty,
        uom: data.uom || TYPE_DEFAULT_UOM[typeNorm] || "kg",
        supplier: data.supplierName || data.supplier || "",
        storageLocation: data.storageLocation || "",
        sourceDeliveryId: deliveryId,
        supplierBatch: data.supplierBatch || "",
        type: typeNorm,
    };

    return await createIngredientBatch(batchData, user);
}

/** top-up existing ingredient batch from a delivery */
export async function topUpIngredientBatchFromDelivery(
    deliveryId,
    ingredientBatchId,
    qtyToAllocate,
    user
) {
    const deliveryRef = doc(db, "Deliveries", deliveryId);
    const snap = await getDoc(deliveryRef);
    if (!snap.exists()) throw new Error("Delivery not found");
    const data = snap.data();
    const remaining = Number(data.remainingQty ?? data.totalQty ?? 0);
    const total = Number(data.totalQty ?? remaining);
    const qty = Number(qtyToAllocate);
    if (remaining < qty) throw new Error("Not enough quantity on delivery");

    const ingRef = doc(db, "IngredientBatches", ingredientBatchId);
    const ingSnap = await getDoc(ingRef);
    if (!ingSnap.exists()) throw new Error("Ingredient batch not found");

    const ing = ingSnap.data();
    const nextRemaining = remaining - qty;

    await updateDoc(deliveryRef, {
        remainingQty: nextRemaining,
        status: deliveryStatusFromRemaining(total, nextRemaining),
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });

    const newRemaining = Number(ing.remainingKg ?? 0) + qty;
    const topUps = Array.isArray(ing.topUps) ? ing.topUps.slice() : [];
    topUps.push({
        deliveryId,
        qty,
        supplierBatch: data.supplierBatch || "",
        date: Timestamp.now(),
    });

    await updateDoc(ingRef, {
        remainingKg: newRemaining,
        topUps,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });

    return ingredientBatchId;
}

/** ------------------------------ Action Logs ------------------------------ */
export async function logAction(batchType, batchId, actionLog, user) {
    const path = `${batchType}/${batchId}/ActionLogs`;
    const ref = collection(db, path);
    const payload = {
        actionId: actionLog.actionId,
        actionName: actionLog.actionName,
        performedBy: userLabel(user),
        performedAt: Timestamp.now(),
        meta: actionLog.meta || {},
    };
    const docRef = await addDoc(ref, payload);
    return docRef.id;
}
export async function listActionLogs(batchType, batchId) {
    const path = `${batchType}/${batchId}/ActionLogs`;
    const snap = await getDocs(collection(db, path));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** ------------------------------ Scanner Lookup + Helpers ------------------------------ */
export async function scannerLookup(raw) {
    const code = String(raw || "").trim();

    // 1) GTIN-13
    if (/^\d{13}$/.test(code)) {
        let sku = null;
        const itemsSnap = await getDocs(
            query(collection(db, "StandardItems"), where("gtin13", "==", code))
        );
        if (!itemsSnap.empty) {
            const d = itemsSnap.docs[0].data();
            sku = d.sku || null;
        }
        if (!sku && HARDCODED_GTIN_SKU[code]) {
            sku = HARDCODED_GTIN_SKU[code];
        }
        return { type: "gtin", gtin13: code, sku };
    }

    // 2) Delivery by DLV-<id>
    if (/^DLV-\w+$/i.test(code)) {
        const id = code.replace(/^DLV-/i, "");
        const ref = doc(db, "Deliveries", id);
        const s = await getDoc(ref);
        if (s.exists()) return { type: "delivery", id };
    }

    // 3) Formatted batch numbers (PROD|INGR-<root>-YYYYMMDD-SEQ)
    if (isBatchNoLike(code)) {
        const parsed = parseBatchNo(code);
        if (parsed?.type === "INGR") {
            const all = await listIngredientBatches();
            const match = all.find((b) => (b.internalBatchNo || b.batchNo) === code);
            if (match) return { type: "ingredientBatch", id: match.id };
        } else {
            const all = await listProductBatches();
            const match = all.find((p) => p.batchNo === code);
            if (match) return { type: "productBatch", id: match.id };
        }
    }

    // 4) Ingredient by internalBatchNo or doc id
    {
        const all = await listIngredientBatches();
        const byNo = all.find((b) => (b.internalBatchNo || b.batchNo) === code);
        if (byNo) return { type: "ingredientBatch", id: byNo.id };
        const byId = all.find((b) => b.id === code);
        if (byId) return { type: "ingredientBatch", id: byId.id };
    }

    // 5) Product by batchNo or doc id
    {
        const all = await listProductBatches();
        const byNo = all.find((p) => p.batchNo === code);
        if (byNo) return { type: "productBatch", id: byNo.id };
        const byId = all.find((p) => p.id === code);
        if (byId) return { type: "productBatch", id: byId.id };
    }

    return { type: "notFound", raw: code };
}

/** Resolve final SKU from a GTIN-13 using StandardItems, with hardcoded fallback */
export async function resolveSkuFromGtin(gtin13) {
    const code = String(gtin13 || "").trim();
    if (!/^\d{13}$/.test(code)) return null;
    let sku = null;
    try {
        const snap = await getDocs(
            query(collection(db, "StandardItems"), where("gtin13", "==", code))
        );
        if (!snap.empty) {
            const d = snap.docs[0].data();
            sku = d.sku || null;
        }
    } catch {
        /* ignore */
    }
    if (!sku) {
        sku = await unifiedProductService.resolveSkuFromGtin(code);
    }
    return sku;
}

/** Given INGR-<root>-YYYYMMDD-SEQ, return a 4-digit sequence "XXXX" (best-effort) */
export function parseSeqFromIngredientBatchNo(batchNo) {
    if (!batchNo) return "";
    try {
        const parsed = parseBatchNo(batchNo);
        if (parsed?.seq != null) return pad4(parsed.seq);
    } catch { /* ignore */ }
    const m = String(batchNo).split("-").pop();
    if (m && /^\d+$/.test(m)) return pad4(m);
    return "";
}

/** Suggest 4-digit consumer code by inspecting 'meat' (and 'fish') allocations of a product batch */
export async function suggestConsumerCodeFromProductBatch(productBatch) {
    try {
        const allocs = Array.isArray(productBatch?.inputAllocations)
            ? productBatch.inputAllocations
            : [];
        if (!allocs.length) return "";

        const itemsSnap = await getDocs(collection(db, "StandardItems"));
        const proteinSet = new Set();
        itemsSnap.forEach((d) => {
            const it = d.data() || {};
            const t = normalizeTypeKey(it.type || "");
            if (t === "meat" || t === "fish") {
                const code = String(it.sku || it.code || it.name || "")
                    .toUpperCase()
                    .trim();
                if (code) proteinSet.add(code);
            }
        });

        for (const a of allocs) {
            const code = String(a.ingredientCode || "").toUpperCase();
            if (!code || !proteinSet.has(code)) continue;
            const batchId = a.ingredientBatchId || a.id;
            if (!batchId) continue;

            const ref = doc(db, "IngredientBatches", batchId);
            const snap = await getDoc(ref);
            if (!snap.exists()) continue;
            const data = snap.data() || {};
            const seq = parseSeqFromIngredientBatchNo(
                data.internalBatchNo || data.batchNo || ""
            );
            if (seq) return seq;
        }
        return "";
    } catch {
        return "";
    }
}

export function formatPackLabel(finalSku, consumerCode) {
    const sku = String(finalSku || "").trim();
    const code = pad4(consumerCode);
    return code ? `${sku}-${code}` : sku;
}

export function computeKgUsedFromPacks(units, packSizeG) {
    const u = Number(units || 0);
    const g = Number(packSizeG || 0);
    if (!isFinite(u) || !isFinite(g)) return 0;
    return (u * g) / 1000;
}

/** Run-out helper */
export async function computeRunoutWarning(
    ingredientBatchId,
    drawQtyKg,
    thresholdKg = 0.5
) {
    const ref = doc(db, "IngredientBatches", ingredientBatchId);
    const snap = await getDoc(ref);
    if (!snap.exists())
        return { willRunout: false, remainingAfter: null, nextCandidate: null };

    const data = snap.data();
    const remaining = Number(data.remainingKg ?? 0);
    const after = remaining - Number(drawQtyKg || 0);
    const willRunout = after <= Number(thresholdKg);

    let nextCandidate = null;
    if (willRunout) {
        const sameCode = (data.ingredientCode || "").toLowerCase();
        const all = await listIngredientBatches();
        const candidates = all
            .filter(
                (b) =>
                    (b.ingredientCode || "").toLowerCase() === sameCode &&
                    b.id !== ingredientBatchId &&
                    Number(b.remainingKg || 0) > 0
            )
            .sort((a, b) => {
                const ar = a.receivedDate?.seconds || 0;
                const br = b.receivedDate?.seconds || 0;
                return ar - br;
            });
        nextCandidate = candidates[0] || null;
    }
    return { willRunout, remainingAfter: after, nextCandidate };
}

export async function getNextBatchCandidateFor(
    ingredientCode,
    excludeBatchId = null
) {
    const all = await listIngredientBatches();
    const candidates = all
        .filter(
            (b) =>
                (b.ingredientCode || "").toLowerCase() ===
                String(ingredientCode || "").toLowerCase() &&
                Number(b.remainingKg || 0) > 0 &&
                (!excludeBatchId || b.id !== excludeBatchId)
        )
        .sort(
            (a, b) =>
                (a.receivedDate?.seconds || 0) - (b.receivedDate?.seconds || 0)
        );
    return candidates[0] || null;
}

/** FIFO alias (single code) */
export async function findOldestEligibleBatch(
    ingredientCode,
    excludeBatchId = null
) {
    return await getNextBatchCandidateFor(ingredientCode, excludeBatchId);
}

/** Bulk FIFO helper */
export async function bulkFindOldestEligibleBatches(codes = []) {
    if (!Array.isArray(codes) || codes.length === 0) return {};
    const all = await listIngredientBatches();
    const result = {};
    const lower = codes.map((c) => String(c || "").toLowerCase());
    for (const code of lower) {
        const cand =
            all
                .filter(
                    (b) =>
                        (b.ingredientCode || "").toLowerCase() === code &&
                        Number(b.remainingKg || 0) > 0
                )
                .sort(
                    (a, b) =>
                        (a.receivedDate?.seconds || 0) - (b.receivedDate?.seconds || 0)
                )[0] || null;
        result[code] = cand;
    }
    return result;
}

/** find open product batches for a skuRoot (remainingKg > 0) */
export async function findOpenProductBatchesBySkuRoot(skuRoot) {
    const all = await listProductBatches();
    const root = deriveSkuRoot(skuRoot || "");
    const matches = all
        .filter(
            (p) =>
                (p.productCode || p.sku || p.skuRoot || "")
                    .toUpperCase()
                    .startsWith(root.toUpperCase()) &&
                Number(p.remainingKg ?? p.qty ?? 0) > 0
        )
        .sort((a, b) => (a.produced?.seconds || 0) - (b.produced?.seconds || 0));
    return matches;
}

/** ensure remainingKg is set on ProductBatch (idempotent) */
export async function ensureProductRemaining(productBatchId) {
    const ref = doc(db, "ProductBatches", productBatchId);
    const s = await getDoc(ref);
    if (!s.exists()) return;
    const d = s.data();
    if (d.remainingKg == null) {
        await updateDoc(ref, {
            remainingKg: Number(d.qty ?? 0),
            updatedAt: Timestamp.now(),
        });
    }
}

/** decrement remainingKg on a ProductBatch (clamped ≥ 0) */
export async function decrementProductBatchRemaining(
    productBatchId,
    deltaKg,
    user
) {
    const ref = doc(db, "ProductBatches", productBatchId);
    const s = await getDoc(ref);
    if (!s.exists()) throw new Error("Product batch not found");
    const d = s.data();
    const cur = Number(d.remainingKg ?? d.qty ?? 0);
    const next = Math.max(0, cur - Number(deltaKg || 0));
    await updateDoc(ref, {
        remainingKg: next,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
    return next;
}

/** append a packaging run to a ProductBatch AND update packaging stock */
export async function createPackagingRun(productBatchId, run, user) {
    console.log(`[Production] Creating packaging run:`, {
        productBatchId,
        run: {
            finalSku: run.finalSku,
            packSize: run.packSize,
            unitsMade: run.unitsMade,
            consumerCode: run.consumerCode,
            kgUsed: run.kgUsed,
            allowUnfinished: run.allowUnfinished
        },
        user: user?.email || user?.displayName || 'system',
        timestamp: new Date().toISOString()
    });

    const ref = doc(db, "ProductBatches", productBatchId);
    const s = await getDoc(ref);
    if (!s.exists()) throw new Error("Product batch not found");
    const d = s.data();

    console.log(`[Production] Product batch details:`, {
        productBatchId,
        skuRoot: d.skuRoot,
        currentStage: d.stage,
        qty: d.qty,
        remainingKg: d.remainingKg,
        existingPackagingRuns: d.packagingRuns?.length || 0
    });

    // Gate: only allow packaging when finished (unless explicitly overridden)
    if ((d.stage || "wip") !== "finished" && !run?.allowUnfinished) {
        throw new Error("Batch not marked finished; complete cure/smoke steps first.");
    }

    const list = Array.isArray(d.packagingRuns) ? d.packagingRuns.slice() : [];
    const entry = {
        finalSku: run.finalSku || "",
        packSize: run.packSize || null,        // grams
        unitsMade: Number(run.unitsMade || 0), // units
        consumerCode: run.consumerCode || null,
        notes: run.notes || null,
        createdAt: Timestamp.now(),
        createdBy: userLabel(user),
    };
    list.push(entry);

    console.log(`[Production] Packaging run entry:`, {
        finalSku: entry.finalSku,
        packSize: entry.packSize,
        unitsMade: entry.unitsMade,
        consumerCode: entry.consumerCode,
        notes: entry.notes,
        createdAt: entry.createdAt.toDate().toISOString()
    });

    await updateDoc(ref, {
        packagingRuns: list,
        updatedAt: Timestamp.now(),
        updatedBy: userLabel(user),
    });

    // Optionally decrement remaining kg if provided
    if (run?.kgUsed) {
        console.log(`[Production] Decrementing remaining kg:`, {
            productBatchId,
            kgUsed: run.kgUsed,
            currentRemainingKg: d.remainingKg,
            newRemainingKg: d.remainingKg - run.kgUsed
        });
        
        await decrementProductBatchRemaining(
            productBatchId,
            Number(run.kgUsed || 0),
            user
        );
    }

    // --- NEW: update packaging stock ---
    if (entry.finalSku && entry.unitsMade) {
        console.log(`[Production] Creating stock from packaging run:`, {
            finalSku: entry.finalSku,
            unitsMade: entry.unitsMade,
            consumerCode: entry.consumerCode,
            packSize: entry.packSize,
            productBatchId
        });

        // A) totals per SKU
        await adjustFinalSkuUnits(entry.finalSku, entry.unitsMade, user);
        // B) per-lot (SKU + 4-digit) if consumer code present
        if (entry.consumerCode) {
            await adjustFinalSkuLotUnits(
                entry.finalSku,
                entry.consumerCode,
                entry.unitsMade,
                { productBatchId, packSize: entry.packSize },
                user
            );
        }
    }

    console.log(`[Production] Packaging run completed successfully:`, {
        productBatchId,
        finalSku: entry.finalSku,
        unitsMade: entry.unitsMade,
        consumerCode: entry.consumerCode,
        packSize: entry.packSize,
        kgUsed: run.kgUsed,
        timestamp: new Date().toISOString()
    });

    return entry;
}

/** lookup a specific packaged run by final SKU + consumer 4-digit code */
export async function findPackagingByConsumerCode(finalSku, consumerCode) {
    const sku = String(finalSku || "").toUpperCase();
    const code = String(consumerCode || "").trim();
    if (!sku || !code) return null;

    const all = await listProductBatches();
    const batch = all.find(
        (b) =>
            Array.isArray(b.packagingRuns) &&
            b.packagingRuns.some(
                (r) =>
                    String(r.finalSku || "").toUpperCase() === sku &&
                    String(r.consumerCode || "").trim() === code
            )
    );
    if (!batch) return null;

    const run =
        batch.packagingRuns.find(
            (r) =>
                String(r.finalSku || "").toUpperCase() === sku &&
                String(r.consumerCode || "").trim() === code
        ) || null;

    return { batch, run };
}

/** full lineage for a packaged code (finalSku + consumerCode) */
export async function getLineageForPackaging(finalSku, consumerCode) {
    const found = await findPackagingByConsumerCode(finalSku, consumerCode);
    if (!found) return null;

    const prod = found.batch;

    const inputs = (prod.inputAllocations || []).map((a) => ({
        ingredientBatchId: a.ingredientBatchId || a.id,
        batchNo: a.batchNo,
        qtyUsedKg: a.qtyUsedKg || a.qtyKg || a.qtyUsed,
        uom: a.uom || "kg",
        supplier: a.supplierName || a.supplier || "",
    }));

    const ingDocs = [];
    for (const i of inputs) {
        if (!i.ingredientBatchId) continue;
        const ref = doc(db, "IngredientBatches", i.ingredientBatchId);
        const s = await getDoc(ref);
        if (s.exists()) ingDocs.push({ id: i.ingredientBatchId, ...s.data(), link: i });
    }

    const logs = await listActionLogs("ProductBatches", prod.id).catch(() => []);

    return {
        productBatch: prod,
        packagingRun: found.run,
        inputs: ingDocs,
        actions: logs,
    };
}

/** ------------------------------ Setup Collections ------------------------------ */
// Suppliers
const suppliersCol = collection(db, "Suppliers");
export async function listSuppliers() {
    const snap = await getDocs(suppliersCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function createSupplier(data, user) {
    const ref = await addDoc(suppliersCol, {
        ...data,
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateSupplier(id, data, user) {
    await updateDoc(doc(db, "Suppliers", id), {
        ...data,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteSupplier(id) {
    await deleteDoc(doc(db, "Suppliers", id));
}

// Delivery Types
const dtypesCol = collection(db, "DeliveryTypes");
export async function listDeliveryTypes() {
    const snap = await getDocs(dtypesCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function createDeliveryType(data, user) {
    const ref = await addDoc(dtypesCol, {
        ...data,
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateDeliveryType(id, data, user) {
    await updateDoc(doc(db, "DeliveryTypes", id), {
        ...data,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteDeliveryType(id) {
    await deleteDoc(doc(db, "DeliveryTypes", id));
}

// Standard Items (master catalog)
const itemsCol = collection(db, "StandardItems");

/**
 * listStandardItems([opts])
 * - If opts.types provided, filters to normalized keys.
 * - Backward compatible: existing callers can still call with no args.
 */
export async function listStandardItems(opts = null) {
    const snap = await getDocs(itemsCol);
    let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (opts && Array.isArray(opts.types) && opts.types.length) {
        const want = new Set(opts.types.map((t) => normalizeTypeKey(t)));
        rows = rows.filter((r) => want.has(normalizeTypeKey(r.type)));
    }
    return rows;
}

/** Light index & helpers to feed dropdowns (reduces free-text) */
export async function listStandardItemsByType(type) {
    const t = normalizeTypeKey(type);
    const all = await listStandardItems();
    return all.filter((i) => normalizeTypeKey(i.type) === t);
}
export async function listStandardCodesByType(types = []) {
    const items = await listStandardItems();
    const normalizedTypes =
        Array.isArray(types) && types.length
            ? types.map((t) => normalizeTypeKey(t))
            : TYPE_KEYS; // default to all known types

    const out = {};
    for (const t of normalizedTypes) out[t] = [];

    for (const it of items || []) {
        const t = normalizeTypeKey(it.type);
        const code = String(it.sku || it.code || it.name || "")
            .toUpperCase()
            .trim();
        if (code && out[t]) out[t].push(code);
    }

    Object.keys(out).forEach((t) => {
        out[t] = Array.from(new Set(out[t])).sort();
    });
    return out;
}
export async function createStandardItem(data, user) {
    const typeNorm = normalizeTypeKey(data.type);
    const ref = await addDoc(itemsCol, {
        ...data,
        type: typeNorm,
        uom: data.uom || TYPE_DEFAULT_UOM[typeNorm] || "kg",
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateStandardItem(id, data, user) {
    const patch = { ...data };
    if (patch.type != null) patch.type = normalizeTypeKey(patch.type);
    if (!patch.uom && patch.type) {
        patch.uom = TYPE_DEFAULT_UOM[patch.type] || "kg";
    }
    await updateDoc(doc(db, "StandardItems", id), {
        ...patch,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteStandardItem(id) {
    await deleteDoc(doc(db, "StandardItems", id));
}

/** ------------------------------ Final SKU Paths ------------------------------ */
const LS_FINAL_PATHS_KEY = "finalSkuPaths.v1";

async function _fetchFinalSkuPathsFromCollection(colName) {
    try {
        const snap = await getDocs(collection(db, colName));
        if (snap.empty) return [];
        const roots = new Set();
        snap.forEach((d) => {
            const data = d.data() || {};
            let root =
                data.root ||
                data.skuRoot ||
                (data.sku ? deriveSkuRoot(String(data.sku)) : null);

            if (!root) {
                const id = String(d.id || "");
                if (/^[A-Z0-9]{2}-[A-Z0-9]{4,}$/i.test(id)) root = id.toUpperCase();
            }
            if (root) roots.add(String(root).toUpperCase());
        });
        return Array.from(roots).sort();
    } catch {
        return [];
    }
}

export async function listFinalSkuPaths() {
    let roots = await _fetchFinalSkuPathsFromCollection("FinalSkuPaths");
    if (!roots.length) {
        roots = await _fetchFinalSkuPathsFromCollection("finalSkuPaths");
    }
    if (roots.length) return roots;

    try {
        const raw = localStorage.getItem(LS_FINAL_PATHS_KEY);
        if (raw) {
            const arr = JSON.parse(raw);
            const set = new Set();
            for (const p of arr || []) {
                if (!p) continue;
                if (typeof p === "string") set.add(p.toUpperCase());
                else if (p.root) set.add(String(p.root).toUpperCase());
                else if (p.sku) {
                    const r = deriveSkuRoot(String(p.sku));
                    if (r) set.add(r.toUpperCase());
                }
            }
            roots = Array.from(set).sort();
            if (roots.length) return roots;
        }
    } catch { /* ignore */ }

    try {
        const items = await listStandardItems();
        const set = new Set();
        for (const it of items || []) {
            if (!it?.sku) continue;
            if (it.type === "final" || /-[A-Z0-9]{3,}-[A-Z]{2,}$/.test(it.sku)) {
                const r = deriveSkuRoot(it.sku);
                if (r) set.add(r.toUpperCase());
            }
        }
        return Array.from(set).sort();
    } catch {
        return [];
    }
}

export async function listFinalSkuRoots() {
    const snap = await getDocs(collection(db, "SkuRoots"));
    return snap.docs
        .map((d) => {
            const data = d.data() || {};
            let root = data.root || d.id;
            return String(root).toUpperCase();
        })
        .sort();
}

export async function upsertFinalSkuRoot(root, user) {
    if (!root) throw new Error("upsertFinalSkuRoot: missing 'root'");
    const payload = {
        root: String(root).toUpperCase(),
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    };
    await setDoc(doc(db, "SkuRoots", payload.root), payload, { merge: true });
    return payload.root;
}
export async function removeFinalSkuRoot(root) {
    const key = String(root || "").toUpperCase();
    if (!key) return false;
    try {
        await deleteDoc(doc(db, "SkuRoots", key));
        return true;
    } catch {
        return false;
    }
}

const actCol = collection(db, "ActionsCatalog");
export async function listActionCatalog() {
    const snap = await getDocs(actCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function createActionDef(data, user) {
    const ref = await addDoc(actCol, {
        ...data,
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateActionDef(id, data, user) {
    await updateDoc(doc(db, "ActionsCatalog", id), {
        ...data,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteActionDef(id) {
    await deleteDoc(doc(db, "ActionsCatalog", id));
}

// Alerts Config
const alertsCol = collection(db, "AlertsConfig");
export async function listAlertRules() {
    const snap = await getDocs(alertsCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function createAlertRule(data, user) {
    const ref = await addDoc(alertsCol, {
        ...data,
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateAlertRule(id, data, user) {
    await updateDoc(doc(db, "AlertsConfig", id), {
        ...data,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteAlertRule(id) {
    await deleteDoc(doc(db, "AlertsConfig", id));
}

/* ──────────────────────────────────────────────────────────────────────────────
   Supplier Barcode Mappings (LEGACY WRAPPERS)
   NOTE: Prefer using src/pages/ProductionToolkit/services/supplierBarcodeService.js
   These remain for backward-compat, and now point at the same collection name.
   ───────────────────────────────────────────────────────────────────────────── */
const mapsCol = collection(db, "SupplierBarcodeMap");
export async function listBarcodeMappings() {
    const snap = await getDocs(mapsCol);
    return snap.docs.map((d) => {
        const data = d.data() || {};
        const t = normalizeTypeKey(data.itemType);
        return {
            id: d.id,
            ...data,
            itemType: t,
            uomDefault: data.uomDefault || TYPE_DEFAULT_UOM[t] || "kg",
        };
    });
}
export async function createBarcodeMapping(data, user) {
    const t = normalizeTypeKey(data.itemType || data.type);
    const ref = await addDoc(mapsCol, {
        ...data,
        itemType: t,
        uomDefault: data.uomDefault || TYPE_DEFAULT_UOM[t] || "kg",
        createdBy: userLabel(user),
        createdAt: Timestamp.now(),
    });
    return ref.id;
}
export async function updateBarcodeMapping(id, data, user) {
    const t = data.itemType != null ? normalizeTypeKey(data.itemType) : undefined;
    const patch = { ...data };
    if (t) {
        patch.itemType = t;
        if (!patch.uomDefault) patch.uomDefault = TYPE_DEFAULT_UOM[t] || "kg";
    }
    await updateDoc(doc(db, "SupplierBarcodeMap", id), {
        ...patch,
        updatedBy: userLabel(user),
        updatedAt: Timestamp.now(),
    });
}
export async function deleteBarcodeMapping(id) {
    await deleteDoc(doc(db, "SupplierBarcodeMap", id));
}
